import { useEffect, useMemo, useState } from "react";
import { Link } from "wouter";
import {
  ArrowLeft, Calendar, Archive, Library as LibraryIcon, Users, FileText,
  Loader2, Plus, X, Mail, AlertTriangle, CheckCircle2, Play, Eye, RefreshCw,
  Download, ChevronUp, ChevronDown, Trash2, Save, Maximize2, History, Edit3,
  Sparkles, Layers, Bot,
} from "lucide-react";

const BASE = import.meta.env.BASE_URL;
const RATEEK = "#FF5C00";

function adminHeaders(): HeadersInit {
  const token = localStorage.getItem("admin_token") || "";
  return { "x-admin-token": token, "Content-Type": "application/json" };
}

// Wrap fetch so any admin-side 401 boots the user back to login
// (instead of leaving stale "Invalid or expired token" banners on every tab).
let _redirecting = false;
async function adminFetch(url: string, init?: RequestInit): Promise<Response> {
  const r = await fetch(url, init);
  if (r.status === 401 && !_redirecting) {
    _redirecting = true;
    try { localStorage.removeItem("admin_token"); } catch {}
    const next = encodeURIComponent(window.location.pathname + window.location.search);
    window.location.replace(`/admin/login?next=${next}`);
  }
  return r;
}

type Tab = "calendar" | "archive" | "library" | "recipients" | "decks" | "platform";

interface Recipient { id: number; email: string; name: string | null; segment: string; status: string; source: string; tags: string[]; lastOpenedAt: string | null; createdAt: string; welcomeSentAt: string | null; }
interface Bite { id: number; kind: string; segment: string; title: string; hook: string; bodyMd: string; action: string; quote: string | null; quoteAuthor: string | null; status: string; useCount: number; lastUsedAt: string | null; updatedAt: string; }
interface Send { id: number; contentId: number | null; recipientEmail: string; segment: string; kind: string; subject: string; sentAt: string; openedAt: string | null; bounced: boolean; errorMessage: string | null; }
interface Run { id: number; ranAt: string; kind: string; totalSent: number; totalFailed: number; totalSkipped: number; warnings: string[]; segmentCounts: any; contentPicked: any; }
interface Deck { id: number; slug: string; title: string; description: string | null; segment: string; status: string; currentVersionNumber: number; updatedAt: string; }
interface CalendarPoint { day: string; segment: string; total: number; failed: number; }

function fmtRel(iso: string | null): string {
  if (!iso) return "—";
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.round(ms / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  return `${d}d ago`;
}

function fmtDateIST(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short", timeZone: "Asia/Kolkata" });
}

function segBadgeClass(seg: string): string {
  if (seg === "investor") return "bg-blue-50 text-blue-700 border-blue-200";
  if (seg === "advisor") return "bg-emerald-50 text-emerald-700 border-emerald-200";
  return "bg-zinc-100 text-zinc-600 border-zinc-200";
}

export default function AdminRateek() {
  const [tab, setTab] = useState<Tab>("calendar");
  const [overview, setOverview] = useState<any>(null);

  const loadOverview = async () => {
    try {
      const r = await adminFetch(`${BASE}api/admin/agents/rateek/overview`, { headers: adminHeaders() });
      if (r.ok) setOverview(await r.json());
    } catch { /* noop */ }
  };
  useEffect(() => { loadOverview(); }, []);

  return (
    <div className="space-y-6 max-w-6xl mx-auto" data-testid="admin-rateek">
      <div>
        <Link href="/admin/agents">
          <span className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 cursor-pointer"><ArrowLeft className="w-3 h-3" /> Agents</span>
        </Link>
      </div>

      <header className="flex items-start gap-4">
        <div className="w-14 h-14 rounded-xl flex items-center justify-center text-white font-bold text-2xl" style={{ background: RATEEK, fontFamily: "Georgia, serif" }}>R</div>
        <div className="flex-1">
          <h1 className="text-2xl font-semibold">Rateek</h1>
          <p className="text-sm text-muted-foreground">Sales Training Agent · Investors & Advisors</p>
          <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <span className="px-2 py-0.5 rounded-full border" style={{ borderColor: `${RATEEK}30`, color: RATEEK }}>Daily 10:30 AM IST</span>
            <span>·</span>
            <span>One short bite per day, per segment. James-Clear voice.</span>
          </div>
        </div>
        <button onClick={loadOverview} className="p-2 rounded-md hover:bg-muted text-muted-foreground" title="Refresh">
          <RefreshCw className="w-4 h-4" />
        </button>
      </header>

      {overview && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {overview.synopsis?.map((s: any, i: number) => (
            <div key={i} className="border rounded-lg p-3 bg-card">
              <div className="text-xs text-muted-foreground uppercase tracking-wide">{s.label}</div>
              <div className="text-2xl font-semibold mt-1">{s.value}</div>
            </div>
          ))}
        </div>
      )}

      <div className="border-b flex gap-1 overflow-x-auto">
        {[
          { k: "calendar", label: "Calendar", Icon: Calendar },
          { k: "archive", label: "Archive", Icon: Archive },
          { k: "library", label: "Library", Icon: LibraryIcon },
          { k: "recipients", label: "Recipients", Icon: Users },
          { k: "decks", label: "Decks", Icon: FileText },
          { k: "platform", label: "Platform", Icon: Layers },
        ].map(({ k, label, Icon }) => (
          <button
            key={k}
            onClick={() => setTab(k as Tab)}
            className={`px-4 py-2 text-sm flex items-center gap-2 border-b-2 -mb-px whitespace-nowrap ${tab === k ? "text-foreground" : "text-muted-foreground hover:text-foreground border-transparent"}`}
            style={tab === k ? { borderColor: RATEEK, color: RATEEK } : undefined}
            data-testid={`rateek-tab-${k}`}
          >
            <Icon className="w-4 h-4" />{label}
          </button>
        ))}
      </div>

      {tab === "calendar" && <CalendarTab />}
      {tab === "archive" && <ArchiveTab />}
      {tab === "library" && <LibraryTab />}
      {tab === "recipients" && <RecipientsTab onChange={loadOverview} />}
      {tab === "decks" && <DecksTab />}
      {tab === "platform" && <PlatformTab />}
    </div>
  );
}

// ---------------- Platform features (Rateek's source of truth) -----------------
type PlatformFeature = {
  id: number; key: string; name: string; audience: "investor" | "advisor" | "both";
  category: "feature" | "agent" | "workflow" | "philosophy";
  description: string; valueProp: string; whereInApp: string | null;
  status: "active" | "coming_soon" | "retired"; sortOrder: number; updatedAt: string;
};
const BLANK_FEATURE: Omit<PlatformFeature, "id" | "updatedAt"> = {
  key: "", name: "", audience: "both", category: "feature",
  description: "", valueProp: "", whereInApp: "", status: "active", sortOrder: 100,
};
function PlatformTab() {
  const [features, setFeatures] = useState<PlatformFeature[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Partial<PlatformFeature> | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    const r = await adminFetch(`${BASE}api/admin/rateek/platform-features`, { headers: adminHeaders() }).then(x => x.json());
    setFeatures(r.features || []); setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!editing) return;
    setErr(null);
    const isNew = !editing.id;
    const url = isNew
      ? `${BASE}api/admin/rateek/platform-features`
      : `${BASE}api/admin/rateek/platform-features/${editing.id}`;
    const r = await adminFetch(url, { method: isNew ? "POST" : "PATCH", headers: adminHeaders(), body: JSON.stringify(editing) });
    const j = await r.json();
    if (!r.ok) { setErr(j.error || "Failed"); return; }
    setEditing(null); load();
  };

  const remove = async (id: number) => {
    if (!confirm("Remove this feature from Rateek's inventory? It won't be in future deck refreshes.")) return;
    await adminFetch(`${BASE}api/admin/rateek/platform-features/${id}`, { method: "DELETE", headers: adminHeaders() });
    load();
  };

  return (
    <div className="space-y-4">
      <div className="border-l-4 p-3 rounded bg-red-50/50 text-sm" style={{ borderColor: RATEEK }}>
        <div className="flex items-start gap-2">
          <Bot className="w-4 h-4 mt-0.5 shrink-0" style={{ color: RATEEK }} />
          <div>
            <div className="font-medium">Rateek's source of truth.</div>
            <div className="text-muted-foreground mt-0.5">This is the inventory Rateek reads every morning at 11:00 AM IST. Add, edit or retire entries here. If anything changes, Rateek refreshes the matching demo deck. If nothing changes, he leaves the deck alone — no busywork.</div>
          </div>
        </div>
      </div>

      <div className="flex justify-between items-center">
        <div className="text-xs text-muted-foreground">{features.length} feature{features.length === 1 ? "" : "s"} · {features.filter(f => f.status === "active").length} active</div>
        <button onClick={() => setEditing({ ...BLANK_FEATURE })} className="text-xs px-3 py-1.5 rounded-md text-white inline-flex items-center gap-1.5" style={{ background: RATEEK }} data-testid="rateek-platform-add">
          <Plus className="w-3 h-3" />Add feature
        </button>
      </div>

      {msg && <div className="text-xs px-3 py-2 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-800">{msg}</div>}

      {loading ? <div className="text-center py-8"><Loader2 className="w-4 h-4 animate-spin inline" /></div> : (
        <div className="border rounded-lg divide-y bg-card">
          {features.length === 0 ? (
            <div className="p-6 text-center text-sm text-muted-foreground">No platform features yet. Add the first one above.</div>
          ) : features.map(f => (
            <div key={f.id} className="p-3 text-sm flex items-start gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium">{f.name}</span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded border ${segBadgeClass(f.audience === "both" ? "" : f.audience)}`}>{f.audience}</span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-700">{f.category}</span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded ${f.status === "active" ? "bg-emerald-100 text-emerald-800" : f.status === "coming_soon" ? "bg-amber-100 text-amber-800" : "bg-zinc-200 text-zinc-500"}`}>{f.status}</span>
                  <span className="text-[10px] text-muted-foreground font-mono">{f.key}</span>
                </div>
                <div className="text-xs text-muted-foreground mt-1">{f.description}</div>
                <div className="text-xs mt-1"><span className="text-muted-foreground">Why care: </span>{f.valueProp}</div>
                {f.whereInApp && <div className="text-[10px] text-muted-foreground font-mono mt-0.5">{f.whereInApp}</div>}
              </div>
              <div className="flex flex-col gap-1 shrink-0">
                <button onClick={() => setEditing(f)} className="text-xs px-2 py-1 rounded border hover:bg-muted inline-flex items-center gap-1"><Edit3 className="w-3 h-3" />Edit</button>
                <button onClick={() => remove(f.id)} className="text-xs px-2 py-1 rounded border hover:bg-red-50 text-red-700 inline-flex items-center gap-1"><Trash2 className="w-3 h-3" />Remove</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="bg-white rounded-lg max-w-xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b flex items-center justify-between"><div className="font-medium">{editing.id ? "Edit feature" : "Add feature"}</div><button onClick={() => setEditing(null)} className="p-1 hover:bg-muted rounded"><X className="w-4 h-4" /></button></div>
            <div className="p-4 space-y-3 text-sm">
              {err && <div className="text-xs px-3 py-2 rounded bg-red-50 border border-red-200 text-red-800">{err}</div>}
              <label className="flex flex-col gap-1"><span className="text-xs text-muted-foreground">Key (lowercase-hyphens, permanent)</span><input className="border rounded px-2 py-1 font-mono" value={editing.key || ""} disabled={!!editing.id} onChange={(e) => setEditing({ ...editing, key: e.target.value.toLowerCase() })} /></label>
              <label className="flex flex-col gap-1"><span className="text-xs text-muted-foreground">Name</span><input className="border rounded px-2 py-1" value={editing.name || ""} onChange={(e) => setEditing({ ...editing, name: e.target.value })} /></label>
              <div className="grid grid-cols-3 gap-2">
                <label className="flex flex-col gap-1"><span className="text-xs text-muted-foreground">Audience</span><select className="border rounded px-2 py-1" value={editing.audience || "both"} onChange={(e) => setEditing({ ...editing, audience: e.target.value as any })}><option>both</option><option>investor</option><option>advisor</option></select></label>
                <label className="flex flex-col gap-1"><span className="text-xs text-muted-foreground">Category</span><select className="border rounded px-2 py-1" value={editing.category || "feature"} onChange={(e) => setEditing({ ...editing, category: e.target.value as any })}><option>feature</option><option>agent</option><option>workflow</option><option>philosophy</option></select></label>
                <label className="flex flex-col gap-1"><span className="text-xs text-muted-foreground">Status</span><select className="border rounded px-2 py-1" value={editing.status || "active"} onChange={(e) => setEditing({ ...editing, status: e.target.value as any })}><option>active</option><option>coming_soon</option><option>retired</option></select></label>
              </div>
              <label className="flex flex-col gap-1"><span className="text-xs text-muted-foreground">What it does (plain language)</span><textarea className="border rounded px-2 py-1" rows={3} value={editing.description || ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} /></label>
              <label className="flex flex-col gap-1"><span className="text-xs text-muted-foreground">Why a learner should care</span><textarea className="border rounded px-2 py-1" rows={2} value={editing.valueProp || ""} onChange={(e) => setEditing({ ...editing, valueProp: e.target.value })} /></label>
              <div className="grid grid-cols-2 gap-2">
                <label className="flex flex-col gap-1"><span className="text-xs text-muted-foreground">Where in app (optional)</span><input className="border rounded px-2 py-1 font-mono text-xs" value={editing.whereInApp || ""} onChange={(e) => setEditing({ ...editing, whereInApp: e.target.value })} /></label>
                <label className="flex flex-col gap-1"><span className="text-xs text-muted-foreground">Sort order</span><input type="number" className="border rounded px-2 py-1" value={editing.sortOrder ?? 100} onChange={(e) => setEditing({ ...editing, sortOrder: parseInt(e.target.value, 10) || 100 })} /></label>
              </div>
            </div>
            <div className="p-4 border-t flex justify-end gap-2"><button onClick={() => setEditing(null)} className="text-xs px-3 py-1.5 rounded border hover:bg-muted">Cancel</button><button onClick={save} className="text-xs px-3 py-1.5 rounded text-white" style={{ background: RATEEK }}>{editing.id ? "Save changes" : "Add feature"}</button></div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------- Calendar -----------------
function CalendarTab() {
  const [points, setPoints] = useState<CalendarPoint[]>([]);
  const [runs, setRuns] = useState<Run[]>([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [runMsg, setRunMsg] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const [c, r] = await Promise.all([
        adminFetch(`${BASE}api/admin/rateek/calendar?days=60`, { headers: adminHeaders() }).then(x => x.json()),
        adminFetch(`${BASE}api/admin/rateek/runs`, { headers: adminHeaders() }).then(x => x.json()),
      ]);
      setPoints(c.points || []);
      setRuns(r.runs || []);
    } catch { /* noop */ }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const triggerRun = async () => {
    if (!confirm("Fire today's training bite NOW to all active recipients?")) return;
    setRunning(true); setRunMsg(null);
    try {
      const r = await adminFetch(`${BASE}api/admin/rateek/run`, { method: "POST", headers: adminHeaders() });
      const j = await r.json();
      if (!r.ok) { setRunMsg(`Failed: ${j?.error || "unknown"}`); }
      else { setRunMsg(`Sent ${j.totalSent} · failed ${j.totalFailed} · skipped ${j.totalSkipped}${j.warnings?.length ? ` · ${j.warnings.length} warning(s)` : ""}`); load(); }
    } catch (e: any) { setRunMsg(`Error: ${e?.message || e}`); }
    setRunning(false);
  };

  const byDay = useMemo(() => {
    const m = new Map<string, { investor: number; advisor: number; failed: number }>();
    for (const p of points) {
      const k = p.day;
      const cur = m.get(k) || { investor: 0, advisor: 0, failed: 0 };
      if (p.segment === "investor") cur.investor += p.total;
      if (p.segment === "advisor") cur.advisor += p.total;
      cur.failed += p.failed;
      m.set(k, cur);
    }
    return m;
  }, [points]);

  const days: string[] = useMemo(() => {
    const out: string[] = [];
    for (let i = 59; i >= 0; i--) {
      const d = new Date(); d.setUTCDate(d.getUTCDate() - i); d.setUTCHours(0, 0, 0, 0);
      out.push(d.toISOString().slice(0, 10));
    }
    return out;
  }, []);

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">Last 60 days · sends per UTC day, split by segment.</p>
        <button onClick={triggerRun} disabled={running} className="text-xs px-3 py-1.5 rounded-md text-white inline-flex items-center gap-1.5 disabled:opacity-50" style={{ background: RATEEK }}>
          {running ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />}
          Run today's bite now
        </button>
      </div>
      {runMsg && <div className="text-xs px-3 py-2 rounded-md bg-zinc-50 border">{runMsg}</div>}

      {loading ? <div className="text-sm text-muted-foreground py-8 text-center"><Loader2 className="w-4 h-4 animate-spin inline" /></div> : (
        <div className="border rounded-lg p-3 bg-card">
          <div className="grid grid-cols-10 gap-1.5">
            {days.map(d => {
              const v = byDay.get(d) || { investor: 0, advisor: 0, failed: 0 };
              const total = v.investor + v.advisor;
              const intensity = Math.min(1, total / 30);
              return (
                <div
                  key={d}
                  title={`${d} — investor: ${v.investor}, advisor: ${v.advisor}${v.failed ? `, failed: ${v.failed}` : ""}`}
                  className="aspect-square rounded border text-[9px] flex flex-col items-center justify-center"
                  style={{ background: total > 0 ? `rgba(185,28,28,${0.1 + intensity * 0.7})` : undefined, color: total > 5 ? "white" : undefined, borderColor: v.failed ? "#fecaca" : undefined }}
                >
                  <div>{new Date(d).getUTCDate()}</div>
                  {total > 0 && <div className="font-semibold">{total}</div>}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div>
        <h3 className="text-sm font-semibold mb-2">Recent runs</h3>
        <div className="border rounded-lg divide-y bg-card">
          {runs.length === 0 ? <div className="p-4 text-sm text-muted-foreground">No runs yet. Cron fires daily at 10:30 AM IST.</div> : runs.map(r => (
            <div key={r.id} className="p-3 text-sm">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <span className={`text-[10px] px-2 py-0.5 rounded uppercase tracking-wide ${r.kind === "manual" ? "bg-amber-100 text-amber-800" : "bg-zinc-100 text-zinc-700"}`}>{r.kind}</span>
                  <span className="font-medium">{fmtDateIST(r.ranAt)}</span>
                </div>
                <div className="text-xs flex items-center gap-3">
                  <span className="text-emerald-700"><CheckCircle2 className="w-3 h-3 inline" /> {r.totalSent} sent</span>
                  {r.totalFailed > 0 && <span className="text-red-700">{r.totalFailed} failed</span>}
                  {r.totalSkipped > 0 && <span className="text-amber-700">{r.totalSkipped} skipped</span>}
                </div>
              </div>
              {Array.isArray(r.warnings) && r.warnings.length > 0 && (
                <div className="mt-1.5 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 flex items-start gap-1.5">
                  <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
                  <div>{r.warnings.join(" · ")}</div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ---------------- Archive -----------------
function ArchiveTab() {
  const [sends, setSends] = useState<Send[]>([]);
  const [seg, setSeg] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState<any | null>(null);

  const load = async () => {
    setLoading(true);
    const url = seg ? `${BASE}api/admin/rateek/archive?segment=${seg}` : `${BASE}api/admin/rateek/archive`;
    const r = await fetch(url, { headers: adminHeaders() }).then(x => x.json());
    setSends(r.sends || []);
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [seg]);

  const openPreview = async (id: number) => {
    const r = await adminFetch(`${BASE}api/admin/rateek/sends/${id}`, { headers: adminHeaders() }).then(x => x.json());
    setPreview(r.send);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 text-sm">
        <span className="text-muted-foreground">Filter:</span>
        {["", "investor", "advisor"].map(s => (
          <button key={s} onClick={() => setSeg(s)} className={`text-xs px-3 py-1 rounded-full border ${seg === s ? "bg-foreground text-background" : "bg-card hover:bg-muted"}`}>{s || "all"}</button>
        ))}
      </div>
      {loading ? <div className="text-center py-8"><Loader2 className="w-4 h-4 animate-spin inline" /></div> : (
        <div className="border rounded-lg divide-y bg-card">
          {sends.length === 0 ? <div className="p-4 text-sm text-muted-foreground">No sends yet.</div> : sends.map(s => (
            <div key={s.id} className="p-3 text-sm flex items-start gap-3">
              <Mail className={`w-4 h-4 mt-0.5 shrink-0 ${s.errorMessage ? "text-red-500" : "text-zinc-400"}`} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-[10px] px-1.5 py-0.5 rounded border ${segBadgeClass(s.segment)}`}>{s.segment}</span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-600 uppercase tracking-wide">{s.kind}</span>
                  <span className="text-xs text-muted-foreground">{fmtDateIST(s.sentAt)}</span>
                </div>
                <div className="font-medium truncate mt-0.5">{s.subject}</div>
                <div className="text-xs text-muted-foreground truncate">→ {s.recipientEmail}</div>
                {s.errorMessage && <div className="text-xs text-red-700 mt-1 bg-red-50 border border-red-200 rounded px-2 py-1">⚠ {s.errorMessage}</div>}
              </div>
              <button onClick={() => openPreview(s.id)} className="text-xs px-2 py-1 rounded border hover:bg-muted inline-flex items-center gap-1"><Eye className="w-3 h-3" />Preview</button>
            </div>
          ))}
        </div>
      )}

      {preview && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setPreview(null)}>
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="p-3 border-b flex items-center justify-between">
              <div className="text-sm font-medium truncate">{preview.subject}</div>
              <button onClick={() => setPreview(null)} className="p-1 hover:bg-muted rounded"><X className="w-4 h-4" /></button>
            </div>
            <div className="overflow-y-auto bg-zinc-50 p-4">
              <div dangerouslySetInnerHTML={{ __html: preview.htmlSnapshot || "<em>(no snapshot)</em>" }} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------- Library -----------------
function LibraryTab() {
  const [bites, setBites] = useState<Bite[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Partial<Bite> | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>("");

  const load = async () => {
    setLoading(true);
    const url = filterStatus ? `${BASE}api/admin/rateek/library?status=${filterStatus}` : `${BASE}api/admin/rateek/library`;
    const r = await fetch(url, { headers: adminHeaders() }).then(x => x.json());
    setBites(r.bites || []);
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterStatus]);

  const save = async () => {
    if (!editing) return;
    const body = JSON.stringify(editing);
    const url = editing.id ? `${BASE}api/admin/rateek/library/${editing.id}` : `${BASE}api/admin/rateek/library`;
    const method = editing.id ? "PATCH" : "POST";
    const r = await fetch(url, { method, headers: adminHeaders(), body });
    if (!r.ok) { const j = await r.json().catch(() => ({})); alert(j.error || "Save failed"); return; }
    setEditing(null); load();
  };

  const setStatus = async (id: number, status: string) => {
    await adminFetch(`${BASE}api/admin/rateek/library/${id}`, { method: "PATCH", headers: adminHeaders(), body: JSON.stringify({ status }) });
    load();
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {["", "draft", "approved", "archived"].map(s => (
            <button key={s} onClick={() => setFilterStatus(s)} className={`text-xs px-3 py-1 rounded-full border ${filterStatus === s ? "bg-foreground text-background" : "bg-card hover:bg-muted"}`}>{s || "all"}</button>
          ))}
        </div>
        <button onClick={() => setEditing({ kind: "principle", segment: "both", status: "draft", title: "", hook: "", bodyMd: "", action: "", quote: "", quoteAuthor: "" })} className="text-xs px-3 py-1.5 rounded-md text-white inline-flex items-center gap-1.5" style={{ background: RATEEK }}>
          <Plus className="w-3 h-3" />New bite
        </button>
      </div>

      {loading ? <div className="text-center py-8"><Loader2 className="w-4 h-4 animate-spin inline" /></div> : (
        <div className="border rounded-lg divide-y bg-card">
          {bites.length === 0 ? <div className="p-4 text-sm text-muted-foreground">No bites yet. Add one to start the cron.</div> : bites.map(b => (
            <div key={b.id} className="p-3 text-sm">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded border ${segBadgeClass(b.segment === "both" ? "" : b.segment)}`}>{b.segment}</span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-600 uppercase tracking-wide">{b.kind}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${b.status === "approved" ? "bg-emerald-100 text-emerald-800" : b.status === "draft" ? "bg-amber-100 text-amber-800" : "bg-zinc-100 text-zinc-500"}`}>{b.status}</span>
                    <span className="text-[10px] text-muted-foreground">used {b.useCount}× · last {fmtRel(b.lastUsedAt)}</span>
                  </div>
                  <div className="font-semibold">{b.title}</div>
                  <div className="text-zinc-700 text-sm mt-0.5">{b.hook}</div>
                  <div className="text-xs text-muted-foreground mt-1 line-clamp-2">{b.bodyMd}</div>
                </div>
                <div className="flex flex-col gap-1 shrink-0">
                  <button onClick={() => setEditing(b)} className="text-xs px-2 py-1 rounded border hover:bg-muted">Edit</button>
                  {b.status === "draft" && <button onClick={() => setStatus(b.id, "approved")} className="text-xs px-2 py-1 rounded text-white" style={{ background: RATEEK }}>Approve</button>}
                  {b.status === "approved" && <button onClick={() => setStatus(b.id, "draft")} className="text-xs px-2 py-1 rounded border hover:bg-muted">Unapprove</button>}
                  {b.status !== "archived" && <button onClick={() => setStatus(b.id, "archived")} className="text-xs px-2 py-1 rounded border hover:bg-muted text-muted-foreground">Archive</button>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b flex items-center justify-between">
              <div className="font-medium">{editing.id ? "Edit bite" : "New bite"}</div>
              <button onClick={() => setEditing(null)} className="p-1 hover:bg-muted rounded"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-4 space-y-3 text-sm">
              <div className="grid grid-cols-3 gap-3">
                <label className="flex flex-col gap-1"><span className="text-xs text-muted-foreground">Segment</span>
                  <select className="border rounded px-2 py-1" value={editing.segment || "both"} onChange={(e) => setEditing({ ...editing, segment: e.target.value })}>
                    <option value="both">both</option><option value="investor">investor</option><option value="advisor">advisor</option>
                  </select>
                </label>
                <label className="flex flex-col gap-1"><span className="text-xs text-muted-foreground">Kind</span>
                  <select className="border rounded px-2 py-1" value={editing.kind || "principle"} onChange={(e) => setEditing({ ...editing, kind: e.target.value })}>
                    <option>principle</option><option>how_to</option><option>story</option><option>quote</option>
                  </select>
                </label>
                <label className="flex flex-col gap-1"><span className="text-xs text-muted-foreground">Status</span>
                  <select className="border rounded px-2 py-1" value={editing.status || "draft"} onChange={(e) => setEditing({ ...editing, status: e.target.value })}>
                    <option>draft</option><option>approved</option><option>archived</option>
                  </select>
                </label>
              </div>
              <label className="flex flex-col gap-1"><span className="text-xs text-muted-foreground">Title (admin-only label)</span>
                <input className="border rounded px-2 py-1" value={editing.title || ""} onChange={(e) => setEditing({ ...editing, title: e.target.value })} maxLength={200} />
              </label>
              <label className="flex flex-col gap-1"><span className="text-xs text-muted-foreground">Hook (becomes the email headline)</span>
                <input className="border rounded px-2 py-1" value={editing.hook || ""} onChange={(e) => setEditing({ ...editing, hook: e.target.value })} maxLength={200} />
              </label>
              <label className="flex flex-col gap-1"><span className="text-xs text-muted-foreground">Body (markdown · 120 words max)</span>
                <textarea className="border rounded px-2 py-1 font-mono text-xs" rows={6} value={editing.bodyMd || ""} onChange={(e) => setEditing({ ...editing, bodyMd: e.target.value })} maxLength={1500} />
                <span className="text-[10px] text-muted-foreground text-right">{(editing.bodyMd || "").length}/1500</span>
              </label>
              <label className="flex flex-col gap-1"><span className="text-xs text-muted-foreground">Today's 5-min action</span>
                <input className="border rounded px-2 py-1" value={editing.action || ""} onChange={(e) => setEditing({ ...editing, action: e.target.value })} maxLength={400} />
              </label>
              <div className="grid grid-cols-3 gap-3">
                <label className="col-span-2 flex flex-col gap-1"><span className="text-xs text-muted-foreground">Quote (optional)</span>
                  <input className="border rounded px-2 py-1" value={editing.quote || ""} onChange={(e) => setEditing({ ...editing, quote: e.target.value })} maxLength={400} />
                </label>
                <label className="flex flex-col gap-1"><span className="text-xs text-muted-foreground">Author</span>
                  <input className="border rounded px-2 py-1" value={editing.quoteAuthor || ""} onChange={(e) => setEditing({ ...editing, quoteAuthor: e.target.value })} maxLength={100} />
                </label>
              </div>
            </div>
            <div className="p-4 border-t flex justify-end gap-2">
              <button onClick={() => setEditing(null)} className="text-xs px-3 py-1.5 rounded border hover:bg-muted">Cancel</button>
              <button onClick={save} className="text-xs px-3 py-1.5 rounded text-white" style={{ background: RATEEK }}>Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------- Recipients -----------------
function RecipientsTab({ onChange }: { onChange: () => void }) {
  const [recipients, setRecipients] = useState<Recipient[]>([]);
  const [loading, setLoading] = useState(true);
  const [seg, setSeg] = useState<string>("");
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", segment: "investor" });
  const [submitMsg, setSubmitMsg] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    const url = seg ? `${BASE}api/admin/rateek/recipients?segment=${seg}` : `${BASE}api/admin/rateek/recipients`;
    const r = await fetch(url, { headers: adminHeaders() }).then(x => x.json());
    setRecipients(r.recipients || []);
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [seg]);

  const add = async () => {
    setSubmitMsg(null);
    const r = await adminFetch(`${BASE}api/admin/rateek/recipients`, { method: "POST", headers: adminHeaders(), body: JSON.stringify(form) });
    const j = await r.json();
    if (!r.ok) { setSubmitMsg(j.error || "Failed"); return; }
    setSubmitMsg(`Added ${j.recipient.email}. Welcome email sent.`);
    setForm({ email: "", name: "", segment: form.segment });
    setAdding(false);
    load(); onChange();
  };

  const setStatus = async (id: number, status: string) => {
    await adminFetch(`${BASE}api/admin/rateek/recipients/${id}`, { method: "PATCH", headers: adminHeaders(), body: JSON.stringify({ status }) });
    load(); onChange();
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {["", "investor", "advisor", "prospect"].map(s => (
            <button key={s} onClick={() => setSeg(s)} className={`text-xs px-3 py-1 rounded-full border ${seg === s ? "bg-foreground text-background" : "bg-card hover:bg-muted"}`}>{s || "all"}</button>
          ))}
        </div>
        <button onClick={() => setAdding(true)} className="text-xs px-3 py-1.5 rounded-md text-white inline-flex items-center gap-1.5" style={{ background: RATEEK }}>
          <Plus className="w-3 h-3" />Add recipient
        </button>
      </div>
      {submitMsg && <div className="text-xs px-3 py-2 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-800">{submitMsg}</div>}

      {loading ? <div className="text-center py-8"><Loader2 className="w-4 h-4 animate-spin inline" /></div> : (
        <div className="border rounded-lg overflow-hidden bg-card">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs text-muted-foreground">
              <tr><th className="text-left px-3 py-2">Email</th><th className="text-left px-3 py-2">Name</th><th className="text-left px-3 py-2">Segment</th><th className="text-left px-3 py-2">Status</th><th className="text-left px-3 py-2">Added</th><th className="px-3 py-2"></th></tr>
            </thead>
            <tbody>
              {recipients.length === 0 ? <tr><td colSpan={6} className="px-3 py-6 text-center text-muted-foreground">No recipients yet.</td></tr> : recipients.map(r => (
                <tr key={r.id} className="border-t">
                  <td className="px-3 py-2 font-mono text-xs">{r.email}</td>
                  <td className="px-3 py-2">{r.name || <span className="text-muted-foreground">—</span>}</td>
                  <td className="px-3 py-2"><span className={`text-[10px] px-1.5 py-0.5 rounded border ${segBadgeClass(r.segment)}`}>{r.segment}</span></td>
                  <td className="px-3 py-2"><span className={`text-[10px] px-1.5 py-0.5 rounded ${r.status === "active" ? "bg-emerald-100 text-emerald-800" : r.status === "paused" ? "bg-amber-100 text-amber-800" : "bg-zinc-100 text-zinc-500"}`}>{r.status}</span></td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">{fmtRel(r.createdAt)}</td>
                  <td className="px-3 py-2 text-right">
                    {r.status !== "active" && <button onClick={() => setStatus(r.id, "active")} className="text-xs px-2 py-0.5 rounded border hover:bg-muted">Resume</button>}
                    {r.status === "active" && <button onClick={() => setStatus(r.id, "paused")} className="text-xs px-2 py-0.5 rounded border hover:bg-muted">Pause</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {adding && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setAdding(false)}>
          <div className="bg-white rounded-lg max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b flex items-center justify-between">
              <div className="font-medium">Add recipient</div>
              <button onClick={() => setAdding(false)} className="p-1 hover:bg-muted rounded"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-4 space-y-3 text-sm">
              <label className="flex flex-col gap-1"><span className="text-xs text-muted-foreground">Email</span>
                <input className="border rounded px-2 py-1" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </label>
              <label className="flex flex-col gap-1"><span className="text-xs text-muted-foreground">Name (optional)</span>
                <input className="border rounded px-2 py-1" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </label>
              <label className="flex flex-col gap-1"><span className="text-xs text-muted-foreground">Segment</span>
                <select className="border rounded px-2 py-1" value={form.segment} onChange={(e) => setForm({ ...form, segment: e.target.value })}>
                  <option value="investor">investor</option><option value="advisor">advisor</option><option value="prospect">prospect</option>
                </select>
              </label>
              <p className="text-xs text-muted-foreground">A one-off "Welcome from Rateek" email will be sent immediately. They'll then join the daily 10:30 AM IST cron from tomorrow.</p>
            </div>
            <div className="p-4 border-t flex justify-end gap-2">
              <button onClick={() => setAdding(false)} className="text-xs px-3 py-1.5 rounded border hover:bg-muted">Cancel</button>
              <button onClick={add} className="text-xs px-3 py-1.5 rounded text-white" style={{ background: RATEEK }}>Add & send welcome</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------- Decks (V1.5) -----------------
type SlideLayout = "title" | "content" | "quote" | "closing";
interface Slide { layout: SlideLayout; title?: string; body?: string; bullets?: string[]; notes?: string; quote?: string; quoteAuthor?: string; }
interface DeckVersion { id: number; versionNumber: number; authoredByAdminEmail: string | null; changeNote: string | null; createdAt: string; slides?: Slide[]; theme?: any; }

const BLANK_SLIDE: Record<SlideLayout, Slide> = {
  title: { layout: "title", title: "Untitled deck", body: "" },
  content: { layout: "content", title: "Section heading", body: "", bullets: [""] },
  quote: { layout: "quote", quote: "", quoteAuthor: "" },
  closing: { layout: "closing", title: "Thank you.", body: "" },
};

function DecksTab() {
  const [decks, setDecks] = useState<Deck[]>([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ slug: "", title: "", description: "", segment: "both" });
  const [msg, setMsg] = useState<string | null>(null);
  const [openDeckId, setOpenDeckId] = useState<number | null>(null);
  const [rateekSlugs, setRateekSlugs] = useState<Record<string, string>>({});
  const [refreshing, setRefreshing] = useState<string | null>(null); // audience currently refreshing

  const load = async () => {
    setLoading(true);
    const [r, s] = await Promise.all([
      adminFetch(`${BASE}api/admin/rateek/decks`, { headers: adminHeaders() }).then(x => x.json()),
      adminFetch(`${BASE}api/admin/rateek/canonical-deck-slugs`, { headers: adminHeaders() }).then(x => x.json()),
    ]);
    setDecks(r.decks || []);
    setRateekSlugs(s.slugs || {});
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const isRateekOwned = (slug: string) => Object.values(rateekSlugs).includes(slug);
  const audienceFor = (slug: string): "investor" | "advisor" | null => {
    for (const [aud, sl] of Object.entries(rateekSlugs)) if (sl === slug) return aud as any;
    return null;
  };

  const refresh = async (audience: "investor" | "advisor", force: boolean) => {
    setMsg(null); setRefreshing(audience);
    try {
      const r = await adminFetch(`${BASE}api/admin/rateek/decks/refresh`, {
        method: "POST", headers: adminHeaders(),
        body: JSON.stringify({ audience, force }),
      });
      const j = await r.json();
      if (!r.ok) { setMsg(`Refresh failed: ${j.error || "unknown error"}`); return; }
      const result = (j.results || [])[0];
      if (result?.status === "generated") setMsg(`Rateek refreshed the ${audience} deck → v${result.newVersionNumber}. ${result.changeSummary || ""}`);
      else if (result?.status === "skipped_no_change") setMsg(`No platform changes since the last ${audience} version. Rateek left it alone.`);
      else if (result?.status === "skipped_no_features") setMsg(`No platform features tagged for ${audience} yet. Add some on the Platform tab first.`);
      else setMsg(`Rateek run finished: ${result?.status || "?"} (${result?.reason || ""})`);
      load();
    } finally { setRefreshing(null); }
  };

  const add = async () => {
    setMsg(null);
    const r = await adminFetch(`${BASE}api/admin/rateek/decks`, { method: "POST", headers: adminHeaders(), body: JSON.stringify(form) });
    const j = await r.json();
    if (!r.ok) { setMsg(j.error || "Failed"); return; }
    setForm({ slug: "", title: "", description: "", segment: "both" });
    setAdding(false); load();
  };

  const downloadPdf = (d: Deck) => {
    if (d.currentVersionNumber < 1) { alert("This deck has no saved version yet — open it and save one first."); return; }
    const tok = localStorage.getItem("admin_token") || "";
    adminFetch(`${BASE}api/admin/rateek/decks/${d.id}/pdf?v=${d.currentVersionNumber}`, { headers: { "x-admin-token": tok } })
      .then(async (r) => {
        if (!r.ok) { alert(`PDF failed: HTTP ${r.status}`); return; }
        const blob = await r.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = `${d.slug}-v${d.currentVersionNumber}.pdf`;
        document.body.appendChild(a); a.click(); a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      });
  };

  return (
    <div className="space-y-4">
      <div className="border-l-4 p-3 rounded bg-red-50/50 text-sm" style={{ borderColor: RATEEK }}>
        <div className="flex items-start gap-2">
          <Sparkles className="w-4 h-4 mt-0.5 shrink-0" style={{ color: RATEEK }} />
          <div className="flex-1">
            <div className="font-medium">Rateek owns two demo decks of Favcy Select itself.</div>
            <div className="text-muted-foreground mt-0.5">He auto-refreshes them every morning at 11:00 AM IST — but only if the platform inventory changed. Use the buttons below to refresh on demand.</div>
            <div className="flex gap-2 mt-2 flex-wrap">
              <button onClick={() => refresh("investor", true)} disabled={!!refreshing} className="text-xs px-2.5 py-1 rounded text-white inline-flex items-center gap-1.5 disabled:opacity-50" style={{ background: RATEEK }} data-testid="rateek-refresh-investor">
                {refreshing === "investor" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                Refresh investor deck
              </button>
              <button onClick={() => refresh("advisor", true)} disabled={!!refreshing} className="text-xs px-2.5 py-1 rounded text-white inline-flex items-center gap-1.5 disabled:opacity-50" style={{ background: RATEEK }} data-testid="rateek-refresh-advisor">
                {refreshing === "advisor" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                Refresh advisor deck
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">Brand-locked sales decks. Every save creates an immutable version. Download v{"{N}"} as a high-quality PDF — filename includes the version number.</p>
        <button onClick={() => setAdding(true)} className="text-xs px-3 py-1.5 rounded-md text-white inline-flex items-center gap-1.5" style={{ background: RATEEK }}>
          <Plus className="w-3 h-3" />New deck
        </button>
      </div>

      {msg && <div className="text-xs px-3 py-2 rounded-md bg-red-50 border border-red-200 text-red-800">{msg}</div>}

      {loading ? <div className="text-center py-8"><Loader2 className="w-4 h-4 animate-spin inline" /></div> : (
        <div className="border rounded-lg divide-y bg-card">
          {decks.length === 0 ? <div className="p-4 text-sm text-muted-foreground">No decks yet.</div> : decks.map(d => (
            <div key={d.id} className="p-3 text-sm flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium">{d.title}</span>
                  {isRateekOwned(d.slug) && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded inline-flex items-center gap-1 text-white" style={{ background: RATEEK }}>
                      <Sparkles className="w-2.5 h-2.5" />Rateek AI
                    </span>
                  )}
                </div>
                <div className="text-xs text-muted-foreground font-mono">/{d.slug}</div>
                {d.description && <div className="text-xs text-muted-foreground mt-1">{d.description}</div>}
                <div className="flex items-center gap-2 mt-1.5">
                  <span className={`text-[10px] px-1.5 py-0.5 rounded border ${segBadgeClass(d.segment === "both" ? "" : d.segment)}`}>{d.segment}</span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded ${d.status === "published" ? "bg-emerald-100 text-emerald-800" : d.status === "archived" ? "bg-zinc-200 text-zinc-500" : "bg-amber-100 text-amber-800"}`}>{d.status}</span>
                  <span className="text-[10px] text-muted-foreground">v{d.currentVersionNumber}</span>
                  <span className="text-[10px] text-muted-foreground">· updated {fmtRel(d.updatedAt)}</span>
                </div>
              </div>
              <div className="flex flex-col gap-1 shrink-0">
                <button onClick={() => setOpenDeckId(d.id)} className="text-xs px-2 py-1 rounded text-white inline-flex items-center gap-1" style={{ background: RATEEK }}><Edit3 className="w-3 h-3" />Open builder</button>
                <button onClick={() => downloadPdf(d)} disabled={d.currentVersionNumber < 1} className="text-xs px-2 py-1 rounded border hover:bg-muted disabled:opacity-40 inline-flex items-center gap-1"><Download className="w-3 h-3" />PDF v{d.currentVersionNumber || 0}</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {adding && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setAdding(false)}>
          <div className="bg-white rounded-lg max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b flex items-center justify-between"><div className="font-medium">New deck</div><button onClick={() => setAdding(false)} className="p-1 hover:bg-muted rounded"><X className="w-4 h-4" /></button></div>
            <div className="p-4 space-y-3 text-sm">
              <label className="flex flex-col gap-1"><span className="text-xs text-muted-foreground">Slug (lowercase, hyphens)</span><input className="border rounded px-2 py-1 font-mono" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase() })} /></label>
              <label className="flex flex-col gap-1"><span className="text-xs text-muted-foreground">Title</span><input className="border rounded px-2 py-1" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></label>
              <label className="flex flex-col gap-1"><span className="text-xs text-muted-foreground">Description (optional)</span><textarea className="border rounded px-2 py-1" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></label>
              <label className="flex flex-col gap-1"><span className="text-xs text-muted-foreground">Segment</span><select className="border rounded px-2 py-1" value={form.segment} onChange={(e) => setForm({ ...form, segment: e.target.value })}><option>both</option><option>investor</option><option>advisor</option></select></label>
            </div>
            <div className="p-4 border-t flex justify-end gap-2"><button onClick={() => setAdding(false)} className="text-xs px-3 py-1.5 rounded border hover:bg-muted">Cancel</button><button onClick={add} className="text-xs px-3 py-1.5 rounded text-white" style={{ background: RATEEK }}>Create</button></div>
          </div>
        </div>
      )}

      {openDeckId !== null && <DeckBuilder deckId={openDeckId} onClose={() => { setOpenDeckId(null); load(); }} />}
    </div>
  );
}

// ---- Deck builder ----
function DeckBuilder({ deckId, onClose }: { deckId: number; onClose: () => void }) {
  const [deck, setDeck] = useState<Deck | null>(null);
  const [slides, setSlides] = useState<Slide[]>([{ ...BLANK_SLIDE.title }]);
  const [activeIdx, setActiveIdx] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [versions, setVersions] = useState<DeckVersion[]>([]);
  const [presentMode, setPresentMode] = useState(false);
  const [changeNote, setChangeNote] = useState("");
  const [showHistory, setShowHistory] = useState(false);

  const load = async () => {
    setLoading(true);
    const [d, v] = await Promise.all([
      adminFetch(`${BASE}api/admin/rateek/decks/${deckId}`, { headers: adminHeaders() }).then(x => x.json()),
      adminFetch(`${BASE}api/admin/rateek/decks/${deckId}/versions`, { headers: adminHeaders() }).then(x => x.json()),
    ]);
    setDeck(d.deck);
    if (d.version?.slides && Array.isArray(d.version.slides) && d.version.slides.length > 0) {
      setSlides(d.version.slides);
    } else {
      setSlides([{ ...BLANK_SLIDE.title, title: d.deck?.title || "Untitled" }]);
    }
    setVersions(v.versions || []);
    setActiveIdx(0); setDirty(false);
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [deckId]);

  const updateSlide = (patch: Partial<Slide>) => {
    setSlides(prev => prev.map((s, i) => i === activeIdx ? { ...s, ...patch } : s));
    setDirty(true);
  };
  const addSlide = (layout: SlideLayout) => {
    if (slides.length >= 80) { alert("80-slide limit reached. Trim before adding more."); return; }
    setSlides(prev => [...prev, { ...BLANK_SLIDE[layout] }]);
    setActiveIdx(slides.length);
    setDirty(true);
  };
  const deleteSlide = (i: number) => {
    if (slides.length === 1) { alert("A deck must have at least one slide."); return; }
    if (!confirm("Delete this slide?")) return;
    setSlides(prev => prev.filter((_, idx) => idx !== i));
    setActiveIdx(Math.max(0, Math.min(activeIdx, slides.length - 2)));
    setDirty(true);
  };
  const move = (i: number, dir: -1 | 1) => {
    const j = i + dir; if (j < 0 || j >= slides.length) return;
    setSlides(prev => { const c = [...prev]; [c[i], c[j]] = [c[j], c[i]]; return c; });
    setActiveIdx(j); setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    const r = await adminFetch(`${BASE}api/admin/rateek/decks/${deckId}/versions`, {
      method: "POST", headers: adminHeaders(),
      body: JSON.stringify({ slides, theme: { primaryColor: "#FF5C00", fontFamily: "Georgia" }, changeNote: changeNote || null }),
    });
    setSaving(false);
    if (!r.ok) { const j = await r.json().catch(() => ({})); alert(j.error || "Save failed"); return; }
    setChangeNote(""); setDirty(false);
    await load();
  };

  const loadVersion = async (n: number) => {
    if (dirty && !confirm("Discard unsaved edits and load this version?")) return;
    const r = await adminFetch(`${BASE}api/admin/rateek/decks/${deckId}?v=${n}`, { headers: adminHeaders() }).then(x => x.json());
    if (r.version?.slides) { setSlides(r.version.slides); setActiveIdx(0); setDirty(true); setShowHistory(false); }
  };

  const downloadCurrentPdf = () => {
    if (!deck || deck.currentVersionNumber < 1) { alert("Save a version first."); return; }
    const tok = localStorage.getItem("admin_token") || "";
    adminFetch(`${BASE}api/admin/rateek/decks/${deckId}/pdf?v=${deck.currentVersionNumber}`, { headers: { "x-admin-token": tok } })
      .then(async (r) => {
        if (!r.ok) { alert(`PDF failed: HTTP ${r.status}`); return; }
        const blob = await r.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a"); a.href = url; a.download = `${deck.slug}-v${deck.currentVersionNumber}.pdf`;
        document.body.appendChild(a); a.click(); a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      });
  };

  if (loading || !deck) return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center"><Loader2 className="w-6 h-6 text-white animate-spin" /></div>
  );

  if (presentMode) return <PresenterMode slides={slides} onExit={() => setPresentMode(false)} deckId={deckId} versionNumber={deck.currentVersionNumber} />;

  const active = slides[activeIdx];

  return (
    <div className="fixed inset-0 bg-zinc-900/95 z-50 flex flex-col" onKeyDown={(e) => { if (e.key === "Escape" && !dirty) onClose(); }}>
      {/* Top bar */}
      <div className="bg-zinc-950 text-white px-4 py-2.5 flex items-center justify-between border-b border-zinc-800 text-sm shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          <button onClick={() => { if (!dirty || confirm("Discard unsaved edits?")) onClose(); }} className="p-1 hover:bg-zinc-800 rounded"><X className="w-4 h-4" /></button>
          <div className="min-w-0">
            <div className="font-medium truncate">{deck.title}</div>
            <div className="text-[10px] text-zinc-400 font-mono">/{deck.slug} · current v{deck.currentVersionNumber} {dirty && <span className="text-amber-400 ml-1">● unsaved</span>}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <input value={changeNote} onChange={(e) => setChangeNote(e.target.value)} placeholder="Change note (optional)" className="bg-zinc-800 border border-zinc-700 rounded px-2 py-1 text-xs text-white placeholder:text-zinc-500 w-56" />
          <button onClick={save} disabled={saving || !dirty} className="text-xs px-3 py-1.5 rounded text-white inline-flex items-center gap-1.5 disabled:opacity-40" style={{ background: RATEEK }}>
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Save v{deck.currentVersionNumber + 1}
          </button>
          <button onClick={() => setPresentMode(true)} className="text-xs px-3 py-1.5 rounded bg-zinc-800 hover:bg-zinc-700 inline-flex items-center gap-1.5"><Maximize2 className="w-3 h-3" />Present</button>
          <button onClick={downloadCurrentPdf} disabled={deck.currentVersionNumber < 1} className="text-xs px-3 py-1.5 rounded bg-zinc-800 hover:bg-zinc-700 disabled:opacity-40 inline-flex items-center gap-1.5"><Download className="w-3 h-3" />PDF v{deck.currentVersionNumber || 0}</button>
          <button onClick={() => setShowHistory(true)} className="text-xs px-3 py-1.5 rounded bg-zinc-800 hover:bg-zinc-700 inline-flex items-center gap-1.5"><History className="w-3 h-3" />History ({versions.length})</button>
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* Left: slide list */}
        <div className="w-56 bg-zinc-900 border-r border-zinc-800 flex flex-col shrink-0">
          <div className="p-2 border-b border-zinc-800 text-[10px] text-zinc-500 uppercase tracking-wider font-mono">Slides ({slides.length})</div>
          <div className="flex-1 overflow-y-auto p-2 space-y-1.5">
            {slides.map((s, i) => (
              <div
                key={i}
                onClick={() => setActiveIdx(i)}
                className={`group cursor-pointer rounded border-2 p-2 text-xs transition ${i === activeIdx ? "border-red-500 bg-zinc-800" : "border-zinc-800 bg-zinc-850 hover:border-zinc-700"}`}
                style={i === activeIdx ? { borderColor: RATEEK } : undefined}
              >
                <div className="flex items-center justify-between mb-1 text-[9px] text-zinc-500 uppercase tracking-wider">
                  <span>{i + 1} · {s.layout}</span>
                  <div className="opacity-0 group-hover:opacity-100 flex gap-0.5 transition">
                    <button onClick={(e) => { e.stopPropagation(); move(i, -1); }} disabled={i === 0} className="p-0.5 hover:text-white disabled:opacity-30"><ChevronUp className="w-3 h-3" /></button>
                    <button onClick={(e) => { e.stopPropagation(); move(i, 1); }} disabled={i === slides.length - 1} className="p-0.5 hover:text-white disabled:opacity-30"><ChevronDown className="w-3 h-3" /></button>
                    <button onClick={(e) => { e.stopPropagation(); deleteSlide(i); }} className="p-0.5 hover:text-red-400"><Trash2 className="w-3 h-3" /></button>
                  </div>
                </div>
                <div className="text-zinc-200 line-clamp-2 leading-tight">{s.title || s.quote || "(empty)"}</div>
              </div>
            ))}
          </div>
          <div className="p-2 border-t border-zinc-800 grid grid-cols-2 gap-1">
            {(["title", "content", "quote", "closing"] as SlideLayout[]).map(l => (
              <button key={l} onClick={() => addSlide(l)} className="text-[10px] px-2 py-1.5 rounded bg-zinc-800 hover:bg-zinc-700 text-white capitalize">+ {l}</button>
            ))}
          </div>
        </div>

        {/* Center: slide preview */}
        <div className="flex-1 flex items-center justify-center p-8 overflow-y-auto bg-zinc-800">
          <div className="w-full max-w-3xl aspect-[16/9] shadow-2xl">
            <SlidePreview slide={active} />
          </div>
        </div>

        {/* Right: editor */}
        <div className="w-96 bg-white border-l flex flex-col shrink-0 overflow-y-auto">
          <SlideEditor slide={active} onChange={updateSlide} idx={activeIdx} />
        </div>
      </div>

      {/* History panel */}
      {showHistory && (
        <div className="absolute inset-0 bg-black/70 z-10 flex items-start justify-center pt-16" onClick={() => setShowHistory(false)}>
          <div className="bg-white rounded-lg max-w-xl w-full max-h-[70vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b flex items-center justify-between">
              <div className="font-medium">Version history</div>
              <button onClick={() => setShowHistory(false)} className="p-1 hover:bg-muted rounded"><X className="w-4 h-4" /></button>
            </div>
            <div className="divide-y">
              {versions.length === 0 ? <div className="p-4 text-sm text-muted-foreground">No versions saved yet.</div> : versions.map(v => (
                <div key={v.id} className="p-3 flex items-start justify-between gap-3">
                  <div>
                    <div className="text-sm font-semibold">v{v.versionNumber}{v.versionNumber === deck.currentVersionNumber && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800">current</span>}</div>
                    <div className="text-xs text-muted-foreground">{fmtDateIST(v.createdAt)} · {v.authoredByAdminEmail || "unknown"}</div>
                    {v.changeNote && <div className="text-xs italic mt-1">"{v.changeNote}"</div>}
                  </div>
                  <div className="flex flex-col gap-1 shrink-0">
                    <button onClick={() => loadVersion(v.versionNumber)} className="text-xs px-2 py-1 rounded border hover:bg-muted">Load into editor</button>
                    <a href="#" onClick={(e) => {
                      e.preventDefault();
                      const tok = localStorage.getItem("admin_token") || "";
                      adminFetch(`${BASE}api/admin/rateek/decks/${deckId}/pdf?v=${v.versionNumber}`, { headers: { "x-admin-token": tok } })
                        .then(async (r) => { const blob = await r.blob(); const url = URL.createObjectURL(blob); const a = document.createElement("a"); a.href = url; a.download = `${deck.slug}-v${v.versionNumber}.pdf`; a.click(); setTimeout(() => URL.revokeObjectURL(url), 1000); });
                    }} className="text-xs px-2 py-1 rounded text-white text-center inline-flex items-center justify-center gap-1" style={{ background: RATEEK }}><Download className="w-3 h-3" />v{v.versionNumber}.pdf</a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SlideEditor({ slide, onChange, idx }: { slide: Slide; onChange: (p: Partial<Slide>) => void; idx: number }) {
  return (
    <div className="p-4 space-y-3 text-sm">
      <div className="text-xs text-muted-foreground uppercase tracking-wider font-mono">Slide {idx + 1} · {slide.layout}</div>

      {(slide.layout === "title" || slide.layout === "content" || slide.layout === "closing") && (
        <label className="flex flex-col gap-1"><span className="text-xs text-muted-foreground">Title</span>
          <input className="border rounded px-2 py-1.5" value={slide.title || ""} onChange={(e) => onChange({ title: e.target.value })} maxLength={200} />
        </label>
      )}

      {(slide.layout === "title" || slide.layout === "closing" || slide.layout === "content") && (
        <label className="flex flex-col gap-1"><span className="text-xs text-muted-foreground">{slide.layout === "content" ? "Intro paragraph (optional)" : "Subtitle / body"}</span>
          <textarea className="border rounded px-2 py-1.5" rows={3} value={slide.body || ""} onChange={(e) => onChange({ body: e.target.value })} maxLength={500} />
        </label>
      )}

      {slide.layout === "content" && (
        <div className="space-y-1">
          <div className="text-xs text-muted-foreground">Bullets</div>
          {(slide.bullets || []).map((b, i) => (
            <div key={i} className="flex gap-1 items-start">
              <input className="border rounded px-2 py-1 text-xs flex-1" value={b} onChange={(e) => { const cp = [...(slide.bullets || [])]; cp[i] = e.target.value; onChange({ bullets: cp }); }} maxLength={200} />
              <button onClick={() => { const cp = (slide.bullets || []).filter((_, j) => j !== i); onChange({ bullets: cp }); }} className="p-1 hover:bg-muted rounded text-muted-foreground"><X className="w-3 h-3" /></button>
            </div>
          ))}
          <button onClick={() => onChange({ bullets: [...(slide.bullets || []), ""] })} className="text-xs px-2 py-1 rounded border hover:bg-muted inline-flex items-center gap-1"><Plus className="w-3 h-3" />Add bullet</button>
        </div>
      )}

      {slide.layout === "quote" && (
        <>
          <label className="flex flex-col gap-1"><span className="text-xs text-muted-foreground">Quote</span>
            <textarea className="border rounded px-2 py-1.5 italic" rows={4} value={slide.quote || ""} onChange={(e) => onChange({ quote: e.target.value })} maxLength={400} />
          </label>
          <label className="flex flex-col gap-1"><span className="text-xs text-muted-foreground">Author</span>
            <input className="border rounded px-2 py-1.5" value={slide.quoteAuthor || ""} onChange={(e) => onChange({ quoteAuthor: e.target.value })} maxLength={100} />
          </label>
        </>
      )}

      <label className="flex flex-col gap-1 pt-3 border-t">
        <span className="text-xs text-muted-foreground">Presenter notes (not shown on slide / not in PDF)</span>
        <textarea className="border rounded px-2 py-1.5 text-xs font-mono bg-amber-50/40" rows={4} value={slide.notes || ""} onChange={(e) => onChange({ notes: e.target.value })} placeholder="What you'll say while this slide is up. Visible in Present mode only." maxLength={1000} />
      </label>
    </div>
  );
}

function SlidePreview({ slide }: { slide: Slide }) {
  if (slide.layout === "title") return (
    <div className="w-full h-full flex flex-col justify-center px-12" style={{ background: RATEEK, color: "white" }}>
      <div className="text-[10px] tracking-[0.3em] mb-6 opacity-80">FAVCY SELECT</div>
      <h1 className="text-5xl font-bold leading-tight" style={{ fontFamily: "Georgia, serif" }}>{slide.title || <span className="opacity-40">Untitled</span>}</h1>
      {slide.body && <p className="text-xl italic mt-6 opacity-90" style={{ fontFamily: "Georgia, serif" }}>{slide.body}</p>}
    </div>
  );
  if (slide.layout === "content") return (
    <div className="w-full h-full flex flex-col px-12 py-10 bg-stone-50 relative" style={{ borderLeft: `6px solid ${RATEEK}` }}>
      {slide.title && <h2 className="text-3xl font-bold mb-4" style={{ color: RATEEK, fontFamily: "Georgia, serif" }}>{slide.title}</h2>}
      {slide.body && <p className="text-lg text-zinc-800 mb-4" style={{ fontFamily: "Georgia, serif" }}>{slide.body}</p>}
      <ul className="space-y-2.5">
        {(slide.bullets || []).filter(b => b.trim()).map((b, i) => (
          <li key={i} className="flex gap-3 text-base text-zinc-900"><span className="mt-2 w-1.5 h-1.5 rounded-full shrink-0" style={{ background: RATEEK }} /><span>{b}</span></li>
        ))}
      </ul>
    </div>
  );
  if (slide.layout === "quote") return (
    <div className="w-full h-full flex flex-col justify-center items-center px-16 bg-stone-50 text-center relative" style={{ borderLeft: `6px solid ${RATEEK}` }}>
      <div className="text-3xl italic font-semibold leading-snug" style={{ fontFamily: "Georgia, serif", color: "#1A1A1A" }}>"{slide.quote || <span className="opacity-30">Add a quote</span>}"</div>
      {slide.quoteAuthor && <div className="text-base mt-6 font-medium" style={{ color: RATEEK }}>— {slide.quoteAuthor}</div>}
    </div>
  );
  // closing
  return (
    <div className="w-full h-full flex flex-col justify-center items-center px-12 text-center" style={{ background: RATEEK, color: "white" }}>
      <h1 className="text-5xl font-bold" style={{ fontFamily: "Georgia, serif" }}>{slide.title || "Thank you."}</h1>
      {slide.body && <p className="text-lg mt-6 opacity-90 max-w-xl" style={{ fontFamily: "Georgia, serif" }}>{slide.body}</p>}
      <div className="text-[10px] tracking-[0.3em] mt-12 opacity-70">FAVCY SELECT</div>
    </div>
  );
}

function PresenterMode({ slides, onExit, deckId, versionNumber }: { slides: Slide[]; onExit: () => void; deckId: number; versionNumber: number }) {
  const [i, setI] = useState(0);
  const [showNotes, setShowNotes] = useState(true);
  const startRef = useState(() => Date.now())[0];

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onExit();
      else if (e.key === "ArrowRight" || e.key === " " || e.key === "PageDown") setI(x => Math.min(slides.length - 1, x + 1));
      else if (e.key === "ArrowLeft" || e.key === "PageUp") setI(x => Math.max(0, x - 1));
      else if (e.key === "n" || e.key === "N") setShowNotes(v => !v);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      // Track view on exit
      if (versionNumber > 0) {
        const tok = localStorage.getItem("admin_token") || "";
        adminFetch(`${BASE}api/admin/rateek/decks/${deckId}/track-view`, {
          method: "POST", headers: { "x-admin-token": tok, "Content-Type": "application/json" },
          body: JSON.stringify({ version: versionNumber, slidesSeen: i + 1, durationSeconds: Math.round((Date.now() - startRef) / 1000) }),
        }).catch(() => {});
      }
    };
    // eslint-disable-next-line
  }, [slides.length]);

  return (
    <div className="fixed inset-0 bg-black z-[60] flex flex-col">
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-5xl aspect-[16/9] shadow-2xl"><SlidePreview slide={slides[i]} /></div>
      </div>
      {showNotes && slides[i].notes && (
        <div className="bg-zinc-900 text-zinc-200 px-6 py-3 border-t border-zinc-800 max-h-32 overflow-y-auto text-sm">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Notes</div>
          <div className="whitespace-pre-wrap">{slides[i].notes}</div>
        </div>
      )}
      <div className="bg-zinc-950 text-zinc-400 px-6 py-2 flex items-center justify-between text-xs border-t border-zinc-800">
        <button onClick={onExit} className="hover:text-white">✕ Exit (Esc)</button>
        <div>{i + 1} / {slides.length}</div>
        <div className="flex gap-3">
          <button onClick={() => setShowNotes(v => !v)} className="hover:text-white">{showNotes ? "Hide" : "Show"} notes (N)</button>
          <button onClick={() => setI(x => Math.max(0, x - 1))} disabled={i === 0} className="hover:text-white disabled:opacity-30">← Prev</button>
          <button onClick={() => setI(x => Math.min(slides.length - 1, x + 1))} disabled={i === slides.length - 1} className="hover:text-white disabled:opacity-30">Next →</button>
        </div>
      </div>
    </div>
  );
}
